<template>
  <div class="tw-benchmark-controls">
    <div class="tw-benchmark-controls__actions">
      <div class="tw-benchmark-controls__row">
        <div class="tw-benchmark-controls__row-left">
          <div class="tw-benchmark-controls__row">
            <div class="tw-benchmark-controls__total">総件数：{{ totalCount }}件</div>
          </div>
        </div>

        <div class="tw-benchmark-controls__row-right">
          <el-button
            size="small"
            :type="isFilterApplied ? 'primary' : 'default'"
            :plain="isFilterApplied"
            @click="onOpenFilter"
          >
            <i class="mdi mdi-magnify" />{{ filterButtonLabel }}
          </el-button>
          <el-button size="small" @click="onOpenTagManagement"> <i class="mdi mdi-tag" />タグ管理 </el-button>
          <el-button size="small" @click="onOpenTableSetting">
            <i class="mdi mdi-sort"></i>表示列のカスタマイズ
          </el-button>
          <el-switch v-model="previewEnabledModel" active-text="プレビュー表示" @change="onChangePreviewEnabled" />
          <CsvDownload v-if="csvCanDownload" text="" :download="csvDownload" @click.native="onClickCsvDownload" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import CsvDownload from '@/client/components/_atoms/csv_download'
import { TrackingService } from '@/client/services'
import { type SearchFilters } from '@/typings/twitter_benchmark_campaign_posts'

import { type CsvDownloadResult } from '../../../hooks/useTwBenchmarkCsvDownload'
import { useTwBenchmarkPostsStore } from '../../../store/useTwBenchmarkPostsStore'
import { useTwBenchmarkTableSettingsStore } from '../../../store/useTwBenchmarkTableSettingsStore'
import { countAppliedFilterValues } from '../../../utils/searchFilterSummary'

/**
 * ページ上部のコントロールバー
 */
export default defineComponent({
  name: 'TwBenchmarkControlsBar',

  components: {
    CsvDownload
  },

  props: {
    csvCanDownload: {
      type: Boolean,
      default: false
    },
    csvDownload: {
      type: Function as PropType<() => Promise<CsvDownloadResult>>,
      default: undefined
    }
  },

  emits: ['open-filter', 'open-tag-management', 'open-table-setting'],

  setup(_props, { emit }) {
    const tableSettingsStore = useTwBenchmarkTableSettingsStore()
    const postsStore = useTwBenchmarkPostsStore()

    const previewEnabledModel = computed({
      get: () => tableSettingsStore.previewEnabled,
      set: (value: boolean) => tableSettingsStore.setPreviewEnabled(value)
    })

    const totalCount = computed<number>(() => postsStore.totalCount)

    const appliedFilterCount = computed<number>(() =>
      countAppliedFilterValues(postsStore.searchFilters as SearchFilters)
    )

    const isFilterApplied = computed<boolean>(() => appliedFilterCount.value > 0)

    const filterButtonLabel = computed<string>(() =>
      isFilterApplied.value ? `キャンペーン検索 (${appliedFilterCount.value})` : 'キャンペーン検索'
    )

    const onOpenFilter = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|キャンペーン検索')
      emit('open-filter')
    }

    const onOpenTagManagement = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ管理')
      emit('open-tag-management')
    }

    const onOpenTableSetting = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|表示列のカスタマイズ')
      emit('open-table-setting')
    }

    const onChangePreviewEnabled = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|プレビュー表示')
    }

    const onClickCsvDownload = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|CSVダウンロード')
    }

    return {
      previewEnabledModel,
      totalCount,
      isFilterApplied,
      filterButtonLabel,
      onOpenFilter,
      onOpenTagManagement,
      onOpenTableSetting,
      onChangePreviewEnabled,
      onClickCsvDownload
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.tw-benchmark-controls {
  display: flex;
  margin-bottom: 16px;

  &__actions {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-unit * 2;
  }

  &__row {
    display: flex;
    gap: $spacing-unit;
    align-items: center;
    align-self: stretch;
    justify-content: space-between;
  }

  &__row-left,
  &__row-right {
    display: flex;
    gap: $spacing-unit;
    align-items: center;
  }

  &__total {
    font-size: 16px;
    font-weight: bold;
    color: $text-color;
  }
}
</style>
